import { graphql, useFragment } from "react-relay";
import { EventCardFragment$key } from "./__generated__/EventCardFragment.graphql";
import ActivityCard from "./ActivityCard";
import { Dataflow03Icon } from "@/icons";
import { FormattedMessage, FormattedNumber } from "react-intl";
import Reward from "./Reward";
import { Badge } from "@/kit/ui/badge";
import { EventCardFooterFragment$key } from "./__generated__/EventCardFooterFragment.graphql";

const EventCardFragment = graphql`
  fragment EventCardFragment on Event {
    id
    slug
    title
    thumbnail
    visibility
    host {
      ...ActivityCardHostFragment
    }
    competitions {
      totalCount
    }
    ...EventCardFooterFragment
    ...ActivityCardTagListFragment
    ...EventLocationFragment
  }
`;

const EventCardFooterFragment = graphql`
  fragment EventCardFooterFragment on Event {
    rewards(first: 1) {
      totalCount
      edges {
        node {
          ...RewardFragment
        }
      }
    }
  }
`;

interface Props {
  event: EventCardFragment$key;
}

export default function EventCard({ event: eventFragment }: Props) {
  const { title, thumbnail, slug, host, competitions, visibility, ...event } =
    useFragment(EventCardFragment, eventFragment);

  return (
    <ActivityCard
      title={title}
      thumbnail={thumbnail || undefined}
      url={`/challenges/${slug}`}
      host={host}
      tags={event}
      location={event}
      isInviteOnly={visibility === "MEMBERS"}
      overlay={<EventCardOverlay count={competitions.totalCount} />}
      footer={<EventCardFooter event={event} />}
    />
  );
}

interface EventCardOverlayProps {
  count: number;
}

function EventCardOverlay({ count }: EventCardOverlayProps) {
  if (count < 1) return null;
  return (
    <>
      <Dataflow03Icon aria-hidden="true" className="text-white w-4 h-4" />
      <p className="text-white font-medium text-sm leading-5 line-clamp-3">
        <FormattedMessage
          defaultMessage="{count, plural, one {# Track} other {# Tracks}}"
          values={{ count }}
        />
      </p>
    </>
  );
}

interface EventCardFooterProps {
  event: EventCardFooterFragment$key;
}

function EventCardFooter(props: EventCardFooterProps) {
  const {
    rewards: { totalCount, edges: rewards },
  } = useFragment(EventCardFooterFragment, props.event);

  if (rewards.length === 0 && totalCount < 1) return null;

  return (
    <div className="flex justify-start items-center w-full gap-1 ">
      <Reward reward={rewards[0].node} />
      {totalCount - 1 >= 1 && (
        <Badge size="lg" shape="pill">
          <span className="text-grey-700 text-sm font-medium leading-5">
            <FormattedNumber
              value={totalCount - 1}
              notation="compact"
              signDisplay="always"
            />
          </span>
        </Badge>
      )}
    </div>
  );
}
